// hooks/useLeftPanelProps.js
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { selectCoachesData, setSearchValue } from '../../../reducers/dualPanelSelectorReducer'
import { PER_PAGE, DUAL_PANEL_SELECTABLE_TYPES } from '../../../constants/dual-panel-selector-constants'
import { getProgramsDualPanelSelectorThunk } from '../../../actions/programActions'
import { getAllSeries } from '../../../actions/seriesActions'
import { getStudentsDualPanelSelector, getCoachesDualPanelSelector } from '../../../actions/usersActions'
import { toast } from 'react-toastify'
import { categoryCompare } from '../../../utility/programHelper'
import PropTypes from 'prop-types'
import {
  addSelectedProgram, deleteSelectedProgram,
  addSelectedSeries, deleteSelectedSeries,
  addSelectedStudent, deleteSelectedStudent,
  addSelectedCoach, deleteSelectedCoach,
  selectSelectedPrograms, selectSelectedSeries, selectSelectedCoaches, selectSelectedStudents,
  addSelectedStudents,
  addSelectedCoaches,
  resetSelectedStudents,
  resetSelectedCoaches,
} from '../../../reducers/programDashboardReducer'

export const useLeftPanelProps = ({ studentsData, seriesData, programsData, coachesData, communityId,
  selectedStudentsMap, allCoachesSelected, setAllCoachesSelected, allStudentsSelected, setAllStudentsSelected }) => {

  const { t } = useTranslation()
  const dispatch = useDispatch()
  const selectedPrograms = useSelector(selectSelectedPrograms)
  const selectedSeries = useSelector(selectSelectedSeries)
  const selectedStudents = useSelector(selectSelectedStudents)
  const selectedCoaches = useSelector(selectSelectedCoaches)
  const { items: coachArray } = useSelector(selectCoachesData)

  const fetchPrograms = useCallback((page, searchTerm, completed) =>
    dispatch(getProgramsDualPanelSelectorThunk({ page, perPage: PER_PAGE, searchTerm, completed }))
      .unwrap()
      .catch(() => toast.error(t('common.somethingWentWrong'))), [dispatch, t])

  const fetchSeries = useCallback((page, searchTerm, completed) =>
    dispatch(getAllSeries({ filters: { search: searchTerm, completed }, page, perPage: PER_PAGE }))
      .unwrap()
      .catch(() => toast.error(t('common.somethingWentWrong'))), [dispatch, t])

  const fetchStudents = useCallback((page, searchTerm) =>
    dispatch(getStudentsDualPanelSelector({ page, perPage: PER_PAGE, communityId, searchTerm }))
      .unwrap()
      .catch(() => toast.error(t('common.somethingWentWrong'))), [dispatch, communityId, t])

  const fetchAllStudents = async (searchTerm) => {
    const { totalCount } = studentsData.pagination

    const alreadyFetched = studentsData.items.length
    const remainingCount = totalCount - alreadyFetched

    if (remainingCount <= 0) {
      return studentsData.items
    }

    const result = await dispatch(
      getStudentsDualPanelSelector({
        page: 0,
        perPage: totalCount,
        communityId,
        searchTerm,
      })
    ).unwrap()

    return [...studentsData.items, ...result.students]
  }

  const fetchCoaches = useCallback(
    (page, searchTerm) =>
      dispatch(getCoachesDualPanelSelector({ searchTerm, page, perPage: PER_PAGE }))
        .unwrap()
        .catch(() => toast.error(t('common.somethingWentWrong'), { position: toast.POSITION.TOP_RIGHT }))
    , [dispatch, t])

  const handleCheckProgram = (checked, course) => {
    if (checked) {
      dispatch(deleteSelectedProgram(course.id))
    } else {
      dispatch(addSelectedProgram({ ...course, type: DUAL_PANEL_SELECTABLE_TYPES.programsWithAnnouncement }))
    }
  }

  const handleCheckSeries = (checked, series) => {
    if (checked) {
      dispatch(deleteSelectedSeries(series.id))
    } else {
      dispatch(addSelectedSeries({ ...series, type: DUAL_PANEL_SELECTABLE_TYPES.programsWithAnnouncement }))
    }
  }

  const handleCheckStudent = (checked, student) => {
    if (checked) {
      dispatch(deleteSelectedStudent(student.id))
    } else {
      dispatch(addSelectedStudent({ ...student, type: DUAL_PANEL_SELECTABLE_TYPES.students }))
    }
  }

  const handleCheckCoach = (checked, coach) => {
    if (checked) {
      dispatch(deleteSelectedCoach(coach.id))
    } else {
      dispatch(addSelectedCoach({ ...coach, type: DUAL_PANEL_SELECTABLE_TYPES.coaches }))
    }
  }

  const handleSelectAllCoaches = () => {
    if (allCoachesSelected) {
      dispatch(resetSelectedCoaches())
    } else {
      const coachesToAdd = coachArray.map(student => ({
        ...student,
        type: DUAL_PANEL_SELECTABLE_TYPES.coaches,
      }))

      dispatch(addSelectedCoaches(coachesToAdd))
    }
    setAllCoachesSelected(prev => !prev)
  }


  const handleSelectAllStudents = async () => {
    setAllStudentsSelected(prev => !prev)
    if (allStudentsSelected) {
      dispatch(resetSelectedStudents())
    } else {
      const allStudents = await fetchAllStudents(studentsData.searchValue || '')
      const studentsToAdd = allStudents.map(student => ({
        ...student,
        type: DUAL_PANEL_SELECTABLE_TYPES.students,
      }))
      dispatch(addSelectedStudents(studentsToAdd))
    }
  }

  return {
    tabTypes: [DUAL_PANEL_SELECTABLE_TYPES.programs, DUAL_PANEL_SELECTABLE_TYPES.series,
      DUAL_PANEL_SELECTABLE_TYPES.students, DUAL_PANEL_SELECTABLE_TYPES.coaches],
    tabProps: {
      [DUAL_PANEL_SELECTABLE_TYPES.programs]: {
        titleText: t('dashboard.coach.useLeftPanelProps.selectProgramsTitle', { count: selectedPrograms.length }),
        allItems: programsData.items,
        withCompletedSwitcher: true,
        searchValue: programsData.searchValue,
        onSearchValueChange: (searchValue) => dispatch(setSearchValue({ searchValue, key: DUAL_PANEL_SELECTABLE_TYPES.programs })),
        getSelectedItem: (id) => selectedPrograms.find(program => program.id === id),
        fetchItems: fetchPrograms,
        pagination: programsData.pagination,
        onCheckChange: handleCheckProgram,
        itemCategoryTitle: (program) => program.category ? program.category.name : t('dashboard.coach.useLeftPanelProps.otherProgramsCategory'),
        itemCategoryCompare: categoryCompare,
        isLoading: programsData.isLoading,
      },
      [DUAL_PANEL_SELECTABLE_TYPES.series]: {
        titleText: t('dashboard.coach.useLeftPanelProps.selectSeriesTitle', { count: selectedSeries.length }),
        allItems: seriesData.items,
        withCompleted: false,
        searchValue: seriesData.searchValue,
        onSearchValueChange: (searchValue) => dispatch(setSearchValue({ searchValue, key: DUAL_PANEL_SELECTABLE_TYPES.series })),
        getSelectedItem: (id) => selectedSeries.find(series => series.id === id),
        fetchItems: fetchSeries, pagination: seriesData.pagination,
        onCheckChange: handleCheckSeries,
        isLoading: seriesData.isLoading,
      },
      [DUAL_PANEL_SELECTABLE_TYPES.students]: {
        titleText: t('dashboard.coach.useLeftPanelProps.selectStudentsTitle', { count: selectedStudents.length }),
        allItems: studentsData.items,
        searchValue: studentsData.searchValue,
        onSearchValueChange: (searchValue) => dispatch(setSearchValue({ searchValue, key: DUAL_PANEL_SELECTABLE_TYPES.students })),
        getSelectedItem: (id) => selectedStudentsMap[id],
        fetchItems: fetchStudents,
        pagination: studentsData.pagination,
        onCheckChange: handleCheckStudent,
        handleSelectAllItems: handleSelectAllStudents,
        allItemsChecked: allStudentsSelected,
        isLoading: studentsData.isLoading,
      },
      [DUAL_PANEL_SELECTABLE_TYPES.coaches]: {
        titleText: t('dashboard.coach.useLeftPanelProps.selectCoachesTitle', { count: selectedCoaches.length }),
        allItems: coachArray,
        searchValue: coachesData.searchValue,
        onSearchValueChange: (searchValue) =>
          dispatch(setSearchValue({ searchValue, key: DUAL_PANEL_SELECTABLE_TYPES.coaches })),
        getSelectedItem: (id) => selectedCoaches.find(coach => coach.id === id),
        fetchItems: fetchCoaches,
        pagination: coachesData.pagination,
        onCheckChange: handleCheckCoach,
        handleSelectAllItems: handleSelectAllCoaches,
        allItemsChecked: allCoachesSelected,
        isLoading: coachesData.isLoading,
      },
    },
    withSwitcherInsteadOfTabs: false,
    switcherPageTitle: 'Title',
    switcherPageInfoText: 'Text',
  }
}


useLeftPanelProps.PropTypes = {
  studentsData: PropTypes.shape({
    items: PropTypes.array.isRequired,
    pagination: PropTypes.shape({
      totalCount: PropTypes.number.isRequired,
    }).isRequired,
    searchValue: PropTypes.string,
    isLoading: PropTypes.bool,
  }).isRequired,
  seriesData: PropTypes.shape({
    items: PropTypes.array.isRequired,
    pagination: PropTypes.object,
    searchValue: PropTypes.string,
    isLoading: PropTypes.bool,
  }).isRequired,
  programsData: PropTypes.shape({
    items: PropTypes.array.isRequired,
    pagination: PropTypes.object,
    searchValue: PropTypes.string,
    isLoading: PropTypes.bool,
  }).isRequired,
  coachesData: PropTypes.shape({
    items: PropTypes.array.isRequired,
    pagination: PropTypes.object,
    searchValue: PropTypes.string,
    isLoading: PropTypes.bool,
  }).isRequired,
  communityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  selectedStudentsMap: PropTypes.object.isRequired,
  allCoachesSelected: PropTypes.bool.isRequired,
  setAllCoachesSelected: PropTypes.func.isRequired,
  allStudentsSelected: PropTypes.bool.isRequired,
  setAllStudentsSelected: PropTypes.func.isRequired,
}
